import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { COMPANY } from "@/data/site";

const NAV = [
  { label: "Start", to: "hero" },
  { label: "Leistungen", to: "leistungen" },
  { label: "Galerie", to: "galerie" },
  { label: "Preisrechner", to: "rechner" },
  { label: "Über uns", to: "ueber-uns" },
  { label: "Bewertungen", to: "bewertungen" },
  { label: "Kontakt", to: "kontakt" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 250);
    } else {
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 320);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass border-b border-black/5 shadow-sm"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-8">
        <button onClick={() => goTo("hero")} data-testid="nav-logo-btn" aria-label="Zur Startseite">
          <Logo className={scrolled ? "h-10 sm:h-14 md:h-16" : "h-11 sm:h-16 md:h-20"} />
        </button>

        <div className="hidden items-center gap-0.5 xl:flex">
          {NAV.map((item) => (
            <button
              key={item.to}
              onClick={() => goTo(item.to)}
              data-testid={`nav-${item.to}`}
              className={`rounded-full px-3 py-2 text-sm font-semibold transition-colors hover:text-[var(--kilic-blue)] ${
                scrolled ? "text-neutral-700" : "text-white/90"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="nav-call-btn"
            className="group hidden items-center gap-2 rounded-full bg-[var(--kilic-green)] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-green-600/20 transition-transform hover:scale-105 sm:flex"
          >
            <Phone className="h-4 w-4 transition-transform group-hover:rotate-12" />
            Anrufen
          </a>
          <button
            className={`xl:hidden ${scrolled ? "text-neutral-900" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menü"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden glass border-t border-black/5 xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV.map((item) => (
                <button
                  key={item.to}
                  onClick={() => goTo(item.to)}
                  data-testid={`mobile-nav-${item.to}`}
                  className="rounded-xl px-4 py-3 text-left font-semibold text-neutral-800 hover:bg-black/5"
                >
                  {item.label}
                </button>
              ))}
              <a
                href={`tel:${COMPANY.phoneTel}`}
                className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-[var(--kilic-green)] px-4 py-3 font-bold text-white"
              >
                <Phone className="h-4 w-4" /> {COMPANY.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
